/* ============================================================
   component-facets.js  (Al Dar Optics premium filters v1.3.0)

   Instant, native Shopify storefront filtering.
   - Menu values resolve to native Search & Discovery filters
     (filter.v / filter.p params) whenever the store has them, so
     Brand, Category, Gender, Shape etc. combine correctly with
     price and availability.
   - Values with no native match fall back to legacy tag URLs
     (/collections/handle/tag1+tag2).
   - Tags already in the page URL that the sidebar does not show
     (e.g. /collections/all/men+eyeglasses from the mega menu) are
     KEPT when another filter is clicked (v1.1 silently dropped them).
   - On a tag URL, tags that match a native filter value are
     converted to native params automatically, so the sidebar shows
     them ticked and every other filter works on top.
   - Pages whose collection URL carries a query (/collections/vendors?q=X)
     keep that query on every filter request.
   - Section Rendering API keeps results, chips, counts in sync.
   - v1.3.0 LOCAL filters: menu values with no Search & Discovery
     filter (Gender, Shape ...) are matched in the browser against
     a product index (?view=filter-index) built from tags, vendor,
     type, category and gender/shape metafields. They combine with
     native filters (AND across groups, OR inside a group), show live
     counts, chips, and client side pagination. URL keeps mf.<group>.
   - Works together with theme.js (drawer, compare, reveal).
   ============================================================ */
(function () {
  'use strict';

  var root = null;
  var sectionId = '';
  var basePath = '';
  var baseQuery = '';
  var currentPath = '';
  var cache = {};
  var requestId = 0;
  var lastKey = null;
  var priceDebounce = null;
  var bound = false;
  var localState = {};
  var indexCache = {};
  var localReq = 0;
  var localPage = 1;
  var SYN = {
    male: 'men', man: 'men', gent: 'men', gentleman: 'men', gentlemen: 'men',
    female: 'women', woman: 'women', lady: 'women', ladie: 'women',
    child: 'kid', children: 'kid', boy: 'kid', girl: 'kid', junior: 'kid', youth: 'kid',
    rectangular: 'rectangle', cateye: 'cat-eye', 'cat-eyes': 'cat-eye', oversize: 'oversized'
  };

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function getForm() { return document.getElementById('FacetFiltersForm'); }

  function handleize(str) {
    return String(str || '').toLowerCase().trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function fkey(str) {
    var h = handleize(str);
    if (h.length > 3 && h.charAt(h.length - 1) === 's') h = h.slice(0, -1);
    return SYN[h] || h;
  }

  /* ---------------- local filter state ---------------- */

  function localInputs() {
    var form = getForm();
    return form ? $all('input[data-local-group]', form) : [];
  }

  function readLocalFromDom() {
    var st = {};
    localInputs().forEach(function (i) {
      if (!i.checked) return;
      var g = i.getAttribute('data-local-group');
      (st[g] = st[g] || []).push(i.getAttribute('data-local-value'));
    });
    localState = st;
  }

  function readLocalFromUrl(search) {
    var st = {};
    new URLSearchParams(search || '').forEach(function (v, k) {
      if (k.indexOf('mf.') !== 0 || !v) return;
      var g = k.slice(3);
      (st[g] = st[g] || []).push(v);
    });
    localState = st;
  }

  function stripLocal(query) {
    var q = new URLSearchParams(query || '');
    var out = new URLSearchParams();
    q.forEach(function (v, k) { if (k.indexOf('mf.') !== 0) out.append(k, v); });
    return out.toString();
  }

  function localQuery() {
    var q = new URLSearchParams();
    Object.keys(localState).forEach(function (g) {
      localState[g].forEach(function (v) { q.append('mf.' + g, v); });
    });
    return q.toString();
  }

  function localActive() {
    return Object.keys(localState).some(function (g) { return localState[g].length > 0; });
  }

  function restoreLocalInputs() {
    localInputs().forEach(function (i) {
      var g = i.getAttribute('data-local-group');
      var v = i.getAttribute('data-local-value');
      i.checked = !!(localState[g] && localState[g].indexOf(v) > -1);
    });
  }

  /* ---------------- path + params ---------------- */

  function pathTags(pathname) {
    if (!basePath || pathname.indexOf(basePath + '/') !== 0) return [];
    var rest = pathname.slice(basePath.length + 1).split('/')[0];
    if (!rest) return [];
    return rest.split('+').map(function (t) {
      try { return decodeURIComponent(t); } catch (e) { return t; }
    }).filter(Boolean);
  }

  function buildPath() {
    var form = getForm();
    if (!form) return basePath;
    var shown = $all('input[data-facet-tag]', form).map(function (cb) {
      return cb.getAttribute('data-facet-tag');
    });
    var locked = pathTags(currentPath).filter(function (t) {
      return shown.indexOf(handleize(t)) === -1;
    });
    var checked = $all('input[data-facet-tag]', form)
      .filter(function (cb) { return cb.checked; })
      .map(function (cb) { return cb.getAttribute('data-facet-tag'); });
    var tags = locked.concat(checked);
    return tags.length ? basePath + '/' + tags.join('+') : basePath;
  }

  function buildQuery(extra) {
    var form = getForm();
    var clean = new URLSearchParams(baseQuery);
    if (form) {
      new URLSearchParams(new FormData(form)).forEach(function (v, k) {
        if (v !== '' && v !== null) clean.append(k, v);
      });
    }
    (extra || []).forEach(function (pair) { clean.append(pair[0], pair[1]); });
    var sortEl = document.getElementById('SortBy');
    if (sortEl && sortEl.value) clean.set('sort_by', sortEl.value);
    return clean.toString();
  }

  /* ---------------- rendering ---------------- */

  function render(path, query, mode, force) {
    query = stripLocal(query);
    var key = path + '?' + query;
    if (key === lastKey && !force) return;
    lastKey = key;

    var lq = localQuery();
    var fullQuery = query + (lq ? (query ? '&' : '') + lq : '');
    var pageUrl = path + (fullQuery ? '?' + fullQuery : '');
    var fetchUrl = path + '?' + (query ? query + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);
    var id = ++requestId;

    root.classList.add('facets-loading');

    var job = cache[fetchUrl]
      ? Promise.resolve(cache[fetchUrl])
      : fetch(fetchUrl, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('facets fetch failed');
          return r.text();
        }).then(function (t) { cache[fetchUrl] = t; return t; });

    job.then(function (html) {
      if (id !== requestId) return;
      currentPath = path;
      update(html);
      restoreLocalInputs();
      if (mode === 'push') history.pushState({ facets: true }, '', pageUrl);
      else if (mode === 'replace') history.replaceState({ facets: true }, '', pageUrl);
      localPage = 1;
      return applyLocal(path, query, id);
    }).then(function () {
      if (id === requestId) root.classList.remove('facets-loading');
    }).catch(function () {
      window.location.href = pageUrl;
    });
  }

  /* ---------------- local filters (Gender, Shape ...) ---------------- */

  function getIndex(path, query) {
    var q = new URLSearchParams(query || '');
    q.delete('page');
    q.set('view', 'filter-index');
    var url = path + '?' + q.toString();
    if (indexCache[url]) return indexCache[url];
    function page(n) {
      return fetch(url + '&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('index ' + r.status); return r.text(); })
        .then(function (t) { return JSON.parse(t); });
    }
    var p = page(1).then(function (first) {
      var pages = Math.min(first.pages || 1, 40);
      var rest = [];
      for (var n = 2; n <= pages; n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var items = first.items || [];
        more.forEach(function (m) { items = items.concat(m.items || []); });
        items.forEach(function (it) {
          var set = {};
          String(it.t || '').split('|').forEach(function (tok) {
            if (tok && tok.trim()) set[fkey(tok)] = true;
          });
          it.keys = set;
        });
        return items;
      });
    });
    p.catch(function () { delete indexCache[url]; });
    indexCache[url] = p;
    return p;
  }

  function matchesGroup(item, g) {
    var vals = localState[g];
    if (!vals || !vals.length) return true;
    for (var i = 0; i < vals.length; i++) if (item.keys[fkey(vals[i])]) return true;
    return false;
  }

  function matchesAll(item, except) {
    var groups = Object.keys(localState);
    for (var i = 0; i < groups.length; i++) {
      if (groups[i] !== except && !matchesGroup(item, groups[i])) return false;
    }
    return true;
  }

  function applyLocal(path, query, id) {
    var inputs = localInputs();
    if (!inputs.length) return Promise.resolve();
    var lid = ++localReq;
    var active = localActive();
    var p = getIndex(path, query).then(function (items) {
      if (lid !== localReq || (id && id !== requestId)) return;
      paintCounts(items);
      paintLocalChips();
      if (active) paintGrid(items.filter(function (it) { return matchesAll(it, null); }));
    }).catch(function () {
      inputs.forEach(function (i) {
        var c = i.closest('label') && i.closest('label').querySelector('[data-local-count]');
        if (c) c.textContent = '';
      });
    });
    return active ? p : Promise.resolve();
  }

  function paintCounts(items) {
    localInputs().forEach(function (i) {
      var g = i.getAttribute('data-local-group');
      var v = fkey(i.getAttribute('data-local-value'));
      var n = 0;
      items.forEach(function (it) { if (it.keys[v] && matchesAll(it, g)) n++; });
      var li = i.closest('.facets-item');
      var c = li && li.querySelector('[data-local-count]');
      if (c) c.textContent = n;
      if (n === 0 && !i.checked) { i.disabled = true; if (li) li.setAttribute('data-empty', ''); }
      else { i.disabled = false; if (li) li.removeAttribute('data-empty'); }
    });
    // group badges
    $all('details.facets-group[data-facet-key^="menu-"]', getForm()).forEach(function (d) {
      var n = $all('input[data-local-group]:checked', d).length;
      var label = $('.facets-label', d);
      if (!label || !$all('input[data-local-group]', d).length) return;
      var badge = $('.facets-badge[data-local-badge]', label);
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'facets-badge';
          badge.setAttribute('data-local-badge', '');
          label.appendChild(document.createTextNode(' '));
          label.appendChild(badge);
        }
        badge.textContent = n;
        d.setAttribute('open', '');
      } else if (badge) {
        badge.remove();
      }
    });
  }

  function paintGrid(list) {
    var per = parseInt(root.getAttribute('data-per-page'), 10) || 24;
    var total = list.length;
    var pages = Math.max(1, Math.ceil(total / per));
    if (localPage > pages) localPage = pages;
    var start = (localPage - 1) * per;
    var slice = list.slice(start, start + per);

    var grid = $('[data-facets-grid]', root);
    if (grid) {
      grid.innerHTML = slice.map(function (it) { return it.h; }).join('');
      $all('.reveal', grid).forEach(function (el) { el.classList.add('in'); });
    }

    var pag = $('[data-facets-pagination]', root);
    if (pag) {
      if (pages > 1) {
        var html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
        html += localPage > 1
          ? '<a href="#" class="pagination-prev" data-local-page="' + (localPage - 1) + '" aria-label="Previous page">&lsaquo; Previous</a>'
          : '<span class="pagination-prev is-disabled" aria-disabled="true">&lsaquo; Previous</span>';
        html += '<ul class="pagination-list">';
        for (var n = 1; n <= pages; n++) {
          html += n === localPage
            ? '<li><span class="is-current" aria-current="page">' + n + '</span></li>'
            : '<li><a href="#" data-local-page="' + n + '">' + n + '</a></li>';
        }
        html += '</ul>';
        html += localPage < pages
          ? '<a href="#" class="pagination-next" data-local-page="' + (localPage + 1) + '" aria-label="Next page">Next &rsaquo;</a>'
          : '<span class="pagination-next is-disabled" aria-disabled="true">Next &rsaquo;</span>';
        pag.innerHTML = html + '</nav>';
      } else {
        pag.innerHTML = '';
      }
    }

    var empty = $('[data-facets-empty]', root);
    if (empty) empty.hidden = total > 0;

    var word = total === 1 ? ' Product' : ' Products';
    var a = $('[data-facets-count-inline]', root); if (a) a.textContent = total + word;
    var h = $('[data-facets-count-head]', root); if (h) h.textContent = total + word;
    var r = $('[data-facets-range]', root);
    if (r) r.textContent = total ? (start + 1) + '\u2013' + (start + slice.length) + ' of ' + total : '0 of 0';
    var ap = $('[data-facets-apply-count]'); if (ap) ap.textContent = '(' + total + ')';
  }

  function paintLocalChips() {
    var box = $('[data-facets-chips]', root);
    if (!box) return;
    $all('[data-local-chip]', box).forEach(function (c) { c.remove(); });
    var checked = localInputs().filter(function (i) { return i.checked; });
    var row = $('.active-filters', box);
    if (!checked.length) {
      if (row && row.hasAttribute('data-local-row')) row.remove();
      return;
    }
    if (!row) {
      row = document.createElement('div');
      row.className = 'active-filters plp-active-row facets-chips';
      row.setAttribute('data-local-row', '');
      var clear = document.createElement('a');
      clear.href = basePath;
      clear.className = 'text-link facets-chips-clear';
      clear.setAttribute('data-facet-remove', '');
      clear.setAttribute('rel', 'nofollow');
      clear.textContent = 'Clear all';
      row.appendChild(clear);
      box.appendChild(row);
    }
    var clearLink = $('.facets-chips-clear', row);
    checked.forEach(function (i) {
      var group = i.closest('details.facets-group');
      var gl = group ? $('.facets-label', group) : null;
      var gname = gl ? (gl.childNodes[0] ? gl.childNodes[0].textContent.trim() : '') : '';
      var txt = i.closest('label') ? $('.facets-text', i.closest('label')).textContent.trim() : '';
      var chip = document.createElement('a');
      chip.href = '#';
      chip.className = 'active-filter';
      chip.setAttribute('data-local-chip', i.id);
      chip.setAttribute('rel', 'nofollow');
      chip.innerHTML = (gname ? '<span class="facets-chip-group"></span>&nbsp;' : '') + '<span class="lc-text"></span><span class="x" aria-hidden="true">&times;</span>';
      if (gname) chip.querySelector('.facets-chip-group').textContent = gname + ':';
      chip.querySelector('.lc-text').textContent = txt;
      row.insertBefore(chip, clearLink || null);
    });
  }

  function isClearLink(el) {
    return el.classList.contains('facets-clear') || el.classList.contains('facets-chips-clear') ||
      el.classList.contains('facets-footer-clear') || !!el.closest('[data-facets-empty]');
  }

  function renderFromUrl(href, mode, force) {
    var u;
    try { u = new URL(href, window.location.origin); } catch (e) { return; }
    render(u.pathname, u.search.replace(/^\?/, ''), mode, force);
  }

  function update(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var wrap = $('[data-facets-wrap]');
    var newWrap = $('[data-facets-wrap]', doc);
    if (wrap && newWrap) {
      var openKeys = [];
      var closedKeys = [];
      var expandedKeys = [];
      var searchVals = {};
      $all('details.facets-group', wrap).forEach(function (d) {
        var key = d.getAttribute('data-facet-key');
        if (d.open) openKeys.push(key); else closedKeys.push(key);
        if (d.classList.contains('is-expanded')) expandedKeys.push(key);
        var s = $('[data-facet-search]', d);
        if (s && s.value) searchVals[key] = s.value;
      });
      var panel = wrap.closest('.plp-filters');
      var scrollTop = panel ? panel.scrollTop : 0;

      wrap.innerHTML = newWrap.innerHTML;

      $all('details.facets-group', wrap).forEach(function (d) {
        var key = d.getAttribute('data-facet-key');
        if (openKeys.indexOf(key) > -1) d.setAttribute('open', '');
        else if (closedKeys.indexOf(key) > -1) d.removeAttribute('open');
        if (expandedKeys.indexOf(key) > -1) {
          d.classList.add('is-expanded');
          var m = $('[data-more-label]', d);
          if (m) m.textContent = 'Show less';
        }
        if (searchVals[key]) {
          var s = $('[data-facet-search]', d);
          if (s) { s.value = searchVals[key]; applySearch(s); }
        }
      });
      initPrice(wrap);
      if (panel) panel.scrollTop = scrollTop;
    }

    swapInner('[data-facets-chips]', doc);

    var grid = $('[data-facets-grid]', root);
    var newGrid = $('[data-facets-grid]', doc);
    if (grid && newGrid) {
      grid.innerHTML = newGrid.innerHTML;
      $all('.reveal', grid).forEach(function (el) { el.classList.add('in'); });
    }

    swapInner('[data-facets-pagination]', doc);

    var empty = $('[data-facets-empty]', root);
    var newEmpty = $('[data-facets-empty]', doc);
    if (empty && newEmpty) empty.hidden = newEmpty.hasAttribute('hidden');

    ['[data-facets-count-head]', '[data-facets-count-inline]', '[data-facets-range]'].forEach(function (sel) {
      var a = $(sel, root);
      var b = $(sel, doc);
      if (a && b) a.textContent = b.textContent;
    });

    var sortEl = document.getElementById('SortBy');
    var newSort = $('#SortBy', doc);
    if (sortEl && newSort && newSort.value) sortEl.value = newSort.value;
  }

  function swapInner(sel, doc) {
    var a = $(sel, root);
    var b = $(sel, doc);
    if (a && b) a.innerHTML = b.innerHTML;
  }

  /* ---------------- tag URL -> native params ---------------- */

  function upgradeTagUrl() {
    var tags = pathTags(window.location.pathname);
    if (!tags.length) return;
    var probeUrl = basePath + '?' + (baseQuery ? baseQuery + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);
    fetch(probeUrl, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('probe failed');
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var pairs = [];
      var allMatched = tags.every(function (t) {
        var h = handleize(t);
        var input = $all('#FacetFiltersForm input[type="checkbox"][name][data-handle]', doc)
          .filter(function (i) { return i.getAttribute('data-handle') === h; })[0];
        if (!input) return false;
        pairs.push([input.getAttribute('name'), input.value]);
        return true;
      });
      if (!allMatched || !pairs.length) return;
      var q = new URLSearchParams(window.location.search);
      var merged = new URLSearchParams(baseQuery);
      q.forEach(function (v, k) { if (!merged.has(k) || k.indexOf('filter.') === 0) merged.append(k, v); });
      pairs.forEach(function (p) { merged.append(p[0], p[1]); });
      render(basePath, merged.toString(), 'replace');
    }).catch(function () { /* keep the tag page as it is */ });
  }

  /* ---------------- price slider ---------------- */

  function initPrice(scope) {
    $all('[data-facet-price]', scope || document).forEach(function (box) {
      if (box.dataset.bound) return;
      box.dataset.bound = 'true';

      var max = parseFloat(box.getAttribute('data-max')) || 100;
      var lo = $('[data-price-range-min]', box);
      var hi = $('[data-price-range-max]', box);
      var fill = $('[data-price-fill]', box);
      var nMin = $('[data-price-min]', box);
      var nMax = $('[data-price-max]', box);

      function bounds() {
        var a = lo ? Math.min(+lo.value, +hi.value) : (parseFloat(nMin.value) || 0);
        var b = lo ? Math.max(+lo.value, +hi.value) : (parseFloat(nMax.value) || max);
        return [Math.max(0, a), Math.min(max, b)];
      }

      function paint() {
        var ab = bounds();
        if (fill) {
          fill.style.left = (ab[0] / max * 100) + '%';
          fill.style.right = (100 - ab[1] / max * 100) + '%';
        }
      }

      function sync() {
        var ab = bounds();
        if (nMin) nMin.value = ab[0] > 0 ? ab[0] : '';
        if (nMax) nMax.value = ab[1] < max ? ab[1] : '';
        paint();
      }

      if (lo && hi) {
        lo.addEventListener('input', sync);
        hi.addEventListener('input', sync);
        lo.addEventListener('change', function () { render(buildPath(), buildQuery(), 'push'); });
        hi.addEventListener('change', function () { render(buildPath(), buildQuery(), 'push'); });
      }

      function fromNumbers() {
        if (lo && hi) {
          var a = parseFloat(nMin.value);
          var b = parseFloat(nMax.value);
          lo.value = isNaN(a) ? 0 : Math.max(0, Math.min(a, max));
          hi.value = isNaN(b) ? max : Math.max(0, Math.min(b, max));
        }
        paint();
        clearTimeout(priceDebounce);
        priceDebounce = setTimeout(function () { render(buildPath(), buildQuery(), 'push'); }, 600);
      }
      if (nMin) nMin.addEventListener('input', fromNumbers);
      if (nMax) nMax.addEventListener('input', fromNumbers);

      paint();
    });
  }

  /* ---------------- in-group search ---------------- */

  function applySearch(input) {
    var q = (input.value || '').trim().toLowerCase();
    var group = input.closest('.facets-group');
    if (!group) return;
    group.classList.toggle('is-searching', q.length > 0);
    $all('.facets-item', group).forEach(function (li) {
      var txt = $('.facets-text', li);
      var hit = !q || (txt && txt.textContent.toLowerCase().indexOf(q) > -1);
      li.style.display = hit ? '' : 'none';
    });
  }

  /* ---------------- events (delegated so AJAX swaps survive) ---------------- */

  function bind() {
    if (bound) return;
    bound = true;

    document.addEventListener('change', function (e) {
      if (!root || !getForm()) return;
      if (e.target.matches('#FacetFiltersForm input[data-local-group]')) {
        readLocalFromDom();
        render(buildPath(), buildQuery(), 'push', true);
      } else if (e.target.matches('#FacetFiltersForm input[type="checkbox"]')) {
        render(buildPath(), buildQuery(), 'push');
      } else if (e.target.id === 'SortBy') {
        var hidden = $('[data-facets-sort-input]');
        if (hidden) hidden.value = e.target.value;
        render(buildPath(), buildQuery(), 'push');
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target.matches('[data-facet-search]')) applySearch(e.target);
    });

    document.addEventListener('submit', function (e) {
      if (!e.target) return;
      if (e.target.id === 'FacetFiltersForm' || (root && e.target.classList.contains('plp-sort') && root.contains(e.target))) {
        e.preventDefault();
        render(buildPath(), buildQuery(), 'push');
      }
    });

    document.addEventListener('click', function (e) {
      if (!root) return;
      var chip = e.target.closest('[data-local-chip]');
      if (chip && root.contains(chip)) {
        e.preventDefault();
        var inp = document.getElementById(chip.getAttribute('data-local-chip'));
        if (inp) inp.checked = false;
        readLocalFromDom();
        render(buildPath(), buildQuery(), 'push', true);
        return;
      }
      var pageLink = e.target.closest('[data-local-page]');
      if (pageLink && root.contains(pageLink)) {
        e.preventDefault();
        localPage = parseInt(pageLink.getAttribute('data-local-page'), 10) || 1;
        applyLocal(currentPath, stripLocal(lastKey.split('?')[1] || ''), null);
        var top = root.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top: top, behavior: 'smooth' });
        return;
      }
      var removeLink = e.target.closest('[data-facet-remove]');
      if (removeLink && root.contains(removeLink)) {
        e.preventDefault();
        if (isClearLink(removeLink)) localState = {};
        renderFromUrl(removeLink.getAttribute('href'), 'push', true);
        return;
      }
      var more = e.target.closest('[data-facet-more]');
      if (more) {
        var group = more.closest('.facets-group');
        var label = $('[data-more-label]', more);
        var expanded = group.classList.toggle('is-expanded');
        if (label) label.textContent = expanded ? 'Show less' : 'Show more';
      }
    });

    window.addEventListener('popstate', function () {
      if (!root) return;
      readLocalFromUrl(window.location.search);
      renderFromUrl(window.location.href, null, true);
    });
  }

  /* ---------------- init ---------------- */

  function init() {
    root = document.querySelector('[data-plp][data-section-id]');
    if (!root || !getForm()) { root = null; return; }
    sectionId = root.getAttribute('data-section-id');
    var raw = root.getAttribute('data-collection-url') || window.location.pathname;
    var qi = raw.indexOf('?');
    basePath = (qi > -1 ? raw.slice(0, qi) : raw).replace(/\/+$/, '');
    baseQuery = qi > -1 ? raw.slice(qi + 1) : '';
    currentPath = window.location.pathname;
    lastKey = window.location.pathname + '?' + stripLocal(window.location.search.replace(/^\?/, ''));
    cache = {};
    indexCache = {};
    localPage = 1;
    readLocalFromUrl(window.location.search);
    restoreLocalInputs();

    var sortEl = document.getElementById('SortBy');
    if (sortEl) sortEl.removeAttribute('onchange');

    bind();
    initPrice(document);
    upgradeTagUrl();
    if (localInputs().length) {
      var nq = stripLocal(window.location.search.replace(/^\?/, ''));
      if (localActive()) root.classList.add('facets-loading');
      applyLocal(window.location.pathname, nq, null).then(function () {
        root.classList.remove('facets-loading');
      });
    }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
